(() => {
  const benefitAnimationControllers = new WeakMap();

  const stopBenefitAnimation = (section) => {
    const controller = benefitAnimationControllers.get(section);
    if (!controller) return;

    if (controller.observer) controller.observer.disconnect();
    controller.animationFrames.forEach((frame) => window.cancelAnimationFrame(frame));
    benefitAnimationControllers.delete(section);
  };

  const initializeBenefitAnimations = (scope = document, restart = false) => {
    const sections = [];

    if (scope.matches && scope.matches('[data-animate-benefits="true"]')) {
      sections.push(scope);
    }

    if (scope.querySelectorAll) {
      sections.push(...scope.querySelectorAll('[data-animate-benefits="true"]'));
    }

    sections.forEach((section) => {
      if (section.dataset.animationInitialized === 'true' && !restart) return;

      stopBenefitAnimation(section);
      section.dataset.animationInitialized = 'true';
      section.dataset.animationState = 'reset';
      section.classList.add('is-animated');
      section.classList.remove('is-in-view');

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)
      ) {
        section.classList.add('is-in-view');
        section.dataset.animationState = 'complete';
        return;
      }

      const stage =
        window.matchMedia('(max-width: 749px)').matches
          ? section.querySelector('.rrg-vest-benefits__list') || section
          : section.querySelector('.rrg-vest-benefits__stage') || section;
      const controller = {
        observer: null,
        animationFrames: [],
      };

      const firstFrame = window.requestAnimationFrame(() => {
        const secondFrame = window.requestAnimationFrame(() => {
          if (!section.isConnected || !stage.isConnected) return;

          section.dataset.animationState = 'observing';
          controller.observer = new IntersectionObserver(
            (entries) => {
              const visibleEntry = entries.find((entry) => entry.isIntersecting);
              if (!visibleEntry) return;

              section.dataset.animationState = 'playing';
              section.classList.add('is-in-view');
              controller.observer.disconnect();
              controller.observer = null;
            },
            {
              rootMargin: '0px 0px -12% 0px',
              threshold: 0.18,
            }
          );

          controller.observer.observe(stage);
        });

        controller.animationFrames.push(secondFrame);
      });

      controller.animationFrames.push(firstFrame);
      benefitAnimationControllers.set(section, controller);
    });
  };

  initializeBenefitAnimations();

  if (!window.RRGVestBenefitAnimationListener) {
    window.RRGVestBenefitAnimationListener = true;
    document.addEventListener('shopify:section:load', (event) => {
      initializeBenefitAnimations(event.target, true);
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) initializeBenefitAnimations(document, true);
    });
  }

  if (customElements.get('rrg-vest-product-card')) return;

  class RRGVestProductCard extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.imageElement = this.querySelector('.rrg-vest-card__image');
      this.thumbnailButtons = Array.from(this.querySelectorAll('[data-gallery-image]'));
      this.galleryImageRequest = 0;
      this.preloadedGalleryImages = new Set();
      this.baseImage = this.imageElement
        ? {
            src: this.imageElement.getAttribute('src'),
            srcset: this.imageElement.getAttribute('srcset'),
            sizes: this.imageElement.getAttribute('sizes'),
          }
        : null;

      this.thumbnailButtons.forEach((button) => {
        button.addEventListener('click', () => this.handleThumbnailClick(button));
        button.addEventListener('mouseenter', () =>
          this.preloadGalleryImage(button.dataset.galleryImage)
        );
        button.addEventListener('focus', () =>
          this.preloadGalleryImage(button.dataset.galleryImage)
        );
      });

      const variantsScript = this.querySelector('[data-product-variants]');
      if (!variantsScript) return;

      try {
        this.variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.error('RRG vest: unable to read product variants.', error);
        return;
      }

      this.optionButtons = Array.from(this.querySelectorAll('[data-option-index][data-option-value]'));
      this.optionCount = this.optionButtons.reduce(
        (largest, button) => Math.max(largest, Number(button.dataset.optionIndex) + 1),
        0
      );
      this.selections = Array(this.optionCount).fill(null);
      this.variantInput = this.querySelector('[data-variant-id]');
      this.submitButton = this.querySelector('.rrg-vest-card__submit');
      this.priceElement = this.querySelector('[data-card-price]');
      this.basePrice = this.priceElement ? this.priceElement.textContent : '';

      this.optionButtons.forEach((button) => {
        button.addEventListener('click', () => this.handleOptionClick(button));
      });

      if (this.dataset.preselectFirstOption === 'true' && this.optionCount > 0) {
        const firstAvailable = this.variants.find((variant) => variant.available);
        if (firstAvailable && firstAvailable.options && firstAvailable.options[0] != null) {
          this.selections[0] = String(firstAvailable.options[0]);
        }
      }

      this.syncCard();
    }

    handleThumbnailClick(button) {
      const image = button.dataset.galleryImage;
      if (!image) return;

      const requestId = ++this.galleryImageRequest;
      const thumbnailImage = button.querySelector('.rrg-vest-card__thumbnail-image');
      const previewImage = thumbnailImage
        ? thumbnailImage.currentSrc || thumbnailImage.src
        : image;

      this.setMainImage(previewImage);

      const fullImage = new Image();
      fullImage.onload = () => {
        if (this.galleryImageRequest !== requestId || !button.classList.contains('is-active')) {
          return;
        }

        this.setMainImage(image);
      };
      fullImage.src = image;
    }

    handleOptionClick(button) {
      if (button.disabled) return;

      this.galleryImageRequest += 1;
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      this.selections[optionIndex] =
        this.selections[optionIndex] === optionValue ? null : optionValue;
      this.syncCard();
    }

    variantMatchesSelections(variant, candidateIndex = null, candidateValue = null) {
      return this.selections.every((selectedValue, index) => {
        const valueToMatch = index === candidateIndex ? candidateValue : selectedValue;
        return valueToMatch == null || String(variant.options[index]) === String(valueToMatch);
      });
    }

    syncCard() {
      this.optionButtons.forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex);
        const optionValue = button.dataset.optionValue;
        const isSelected = this.selections[optionIndex] === optionValue;
        const isAvailable = this.variants.some(
          (variant) =>
            variant.available &&
            this.variantMatchesSelections(variant, optionIndex, optionValue)
        );

        button.setAttribute('aria-pressed', String(isSelected));
        button.disabled = !isAvailable;
      });

      const selectionComplete = this.selections.every((value) => value != null);
      const selectedVariant =
        this.optionCount === 0
          ? this.variants.find((variant) => variant.available) || this.variants[0]
          : selectionComplete
            ? this.variants.find(
                (variant) =>
                  variant.options.length === this.optionCount &&
                  variant.options.every(
                    (optionValue, index) => String(optionValue) === String(this.selections[index])
                  )
              )
            : null;

      if (this.variantInput) {
        this.variantInput.value = selectedVariant ? selectedVariant.id : '';
      }

      if (this.submitButton) {
        this.submitButton.disabled = !selectedVariant || !selectedVariant.available;
      }

      const visualVariant =
        selectedVariant ||
        this.variants.find(
          (variant) => variant.available && this.variantMatchesSelections(variant)
        );

      if (this.priceElement) {
        this.priceElement.textContent =
          visualVariant && visualVariant.price ? visualVariant.price : this.basePrice;
      }

      if (
        this.imageElement &&
        this.dataset.updateImage === 'true' &&
        visualVariant &&
        visualVariant.image
      ) {
        this.setMainImage(visualVariant.image);
      } else {
        this.restoreBaseImage();
      }
    }

    preloadGalleryImage(source) {
      if (!source || this.preloadedGalleryImages.has(source)) return;

      this.preloadedGalleryImages.add(source);
      const image = new Image();
      image.src = source;
    }

    imageKey(source) {
      if (!source) return '';

      try {
        return new URL(source, window.location.origin).pathname;
      } catch (error) {
        return String(source).split('?')[0];
      }
    }

    setMainImage(source) {
      if (!this.imageElement || !source) return;

      this.imageElement.src = source;
      this.imageElement.removeAttribute('srcset');
      this.imageElement.removeAttribute('sizes');
      this.syncActiveThumbnail(source);
    }

    syncActiveThumbnail(source) {
      if (!this.thumbnailButtons.length) return;

      const activeKey = this.imageKey(source);
      this.thumbnailButtons.forEach((button) => {
        const isActive = this.imageKey(button.dataset.galleryImage) === activeKey;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    restoreBaseImage() {
      if (!this.imageElement || !this.baseImage) return;

      if (this.baseImage.src) this.imageElement.src = this.baseImage.src;
      if (this.baseImage.srcset) {
        this.imageElement.setAttribute('srcset', this.baseImage.srcset);
      } else {
        this.imageElement.removeAttribute('srcset');
      }
      if (this.baseImage.sizes) {
        this.imageElement.setAttribute('sizes', this.baseImage.sizes);
      } else {
        this.imageElement.removeAttribute('sizes');
      }
      this.syncActiveThumbnail(this.baseImage.src);
    }
  }

  customElements.define('rrg-vest-product-card', RRGVestProductCard);
})();
